import { first, firstValueFrom, map, Observable, race, Subject } from "rxjs";
import { ID } from "./id";
import { Component, createContext, h, Node, useContext, useMemo, useRef, useState } from "@lukekaalim/act";
import { Animation, lerp, useAnimation } from "./curve";

export type ModalID = ID<"modal">;
export type Modal<T> = {
  id: ModalID,

  component: ModalComponent<T>,
  options: ModalOptions,

  close(): void,
  submit(value: T): void,

  onClose: Subject<void>,
  onSubmit: Subject<T>,
}

export type ModalOptions = {
  blockBackgroundClickDismiss?: boolean
}

export type ModalComponent<T = never> = Component<{
  modal: Modal<T>,
  manager: ModalManager,

  active: boolean,

  submit(value: T): void,
  close(): void,
}>

export type ModalManager = {
  stack: Modal<unknown>[],

  add<T>(component: ModalComponent<T>, modalOptions?: ModalOptions): Promise<null | T>,

  close(): void,
  clear(): void,
};

export const ModalManagerContext = createContext<ModalManager | null>(null);

export const useModalManager = () => {
  const manager = useContext(ModalManagerContext);
  
  if (!manager)
    throw new Error();
  
  return manager;
}

export const useModalManagerProvider = (): ModalManager => {
  const [stack, setStack] = useState<Modal<unknown>[]>([]);

  return useMemo((): ModalManager => ({
    stack,

    async add<T>(component: ModalComponent<T>, options: ModalOptions = {}) {
      const onClose = new Subject<void>();
      const onSubmit = new Subject<T>();

      const modal: Modal<T> = {
        id: ID('modal'),

        onClose,
        onSubmit,

        component,
        options,

        submit(value) {
          onSubmit.next(value);
        },
        close() {
          onClose.next();
        },
      }
      setStack(stack => [...stack, modal as Modal<unknown>]);

      const finished = race([
        onSubmit,
        onClose.pipe(map(() => null)),
      ])
      
      const value = await firstValueFrom(finished);

      setStack(stack => stack.filter(s => s.id !== modal.id));

      return value;
    },
    close() {
      const activeModal = stack[stack.length - 1];
      if (!activeModal)
        return;

      activeModal.close();
    },
    clear() {
      for (const modal of stack.toReversed())
        modal.close();

      setStack([]);
    },
  }), [stack])
}

export type ModalRendererProps = {
  manager: ModalManager
}

export const ModalRenderer: Component<ModalRendererProps> = ({ manager }) => {
  if (!manager.stack.length)
    return null;
  
  return h('div', { style: { position: 'absolute', top: '0', bottom: '0', left: '0', right: '0', display: 'flex' } }, [
    manager.stack.flatMap((modal, index) => {
      const active = index === manager.stack.length - 1;

      if (active) {
        return [
          h(ModalBackground, { key: 'background', onClick: modal.close }),
          h(ModalContainer, { key: modal.id, active }, h(modal.component, {
            active,
            submit: modal.submit,
            close: modal.close,
            manager, modal
          })),
        ];
      } else {
        return h(ModalContainer, { key: modal.id, active }, h(modal.component, {
          active,
          submit: modal.submit,
          close: modal.close,
          manager, modal
        }))
      }
    })
  ])
}

export const ModalContainer: Component<{ active: boolean }> = ({ children, active }) => {
  return h('div', { style: {
    position: 'absolute',
    margin: 'auto',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    pointerEvents: active ? 'auto' : 'none'
  }}, children)
};
export const ModalBackground: Component<{ onClick(): void }> = ({ onClick }) => {
  const ref = useRef<HTMLElement | null>(null);

  const anim = useRef(Animation(performance.now(), 200, [0, 1], ([a, b], p) => lerp(a, b, p))).get();
  useAnimation(anim, (opacity) => {
    const background = ref.get()!;
    background.style.opacity = opacity.toString();
  })

  return h('div', { onClick, ref, style: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(4px)'
  } })
}